import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = {
  key: 0,
  id: "app_loader",
  class: "loading"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("span", { class: "loader" }, null, -1)
const _hoisted_3 = [
  _hoisted_2
]
const _hoisted_4 = {
  key: 1,
  class: "alert-box--error"
}
const _hoisted_5 = { key: 2 }
const _hoisted_6 = ["src"]

import { ref, onMounted } from 'vue'
import axios from 'axios'
interface ElevenLabsProject {
  project_id: string;
  name: string;
  create_date_unix: number;
}

// Elevenlabs Configuration Data

const __default__ = { name: 'AudioPlayer' }

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  audioEnabled: {
    type: Boolean,
    required: true,
  },
  elevenlabsProjectId: {
    type: String,
    default: '',
  },
  elevenlabsChapterId: {
    type: String,
    default: '',
  },
  bodyContent: {
    type: String,
    required: true,
  },
  articleTitle: {
    type: String,
    required: true,
  },
},
  setup(__props) {

const props = __props



const apiKey = ref(process.env.ELEVENLABS_API_KEY) // API Key is generated inside ElevenLabs dashboard (My Account -> API Keys)
const audioEnabled = ref(props.audioEnabled ?? null) // Disabled by default. Enabled per article from within Craft's admin
const maxArticleCharAllowed = ref(30000)

// Elevenlabs Project Data
const projectName = ref(window.location.href)
const projectId = ref<string | undefined>()
const chapterId = ref<string | undefined>()
const modelId = ref('eleven_turbo_v2')
const audioVoiceId = ref('21m00Tcm4TlvDq8ikWAM')

// Article Data
const articleTitle = ref(props.articleTitle)
const bodyContent = ref(props.bodyContent)
const articleCharCount = ref(props.articleTitle.length + props.bodyContent.length)

const projects = ref<ElevenLabsProject[]>([])
const audioUrl = ref<string>('')
const errorMessage = ref('There has been an error while generating the audio. Please try again later.')
const BUTTON_TEXT = 'Listen Here'

// UI State Flags
const showGenerateAudioBtn = ref(!!audioEnabled.value)
const showAudioPlayer = ref(false)
const loading = ref(false)
const hasError = ref(false)

/**
 * Returns a single ElevenLabs project object
 */
const getProject = async (projectName: string): Promise<ElevenLabsProject | null> => {
  try {
    // Get all projects from Elevenlabs
    const response = await axios.get(
      getApiEndpoint('projects'), 
      {
        headers: {
          'xi-api-key': apiKey.value,
        },
      })

    projects.value = response.data.projects || []

    // Return the article's matching project or null
    return projects.value.find(project => project.name === projectName) || null
  }
  catch(error) {
    setError(`Error fetching projects: ${error}`)
    return null
  }
}

/**
 * Creates a new project in Elevenlabs Studio
 */
const createProject = async (projectName: string, audioVoiceId: string, modelId: string): Promise<ElevenLabsProject | null> => {
  try {
    // Set required data for Studio API
    const formData = new FormData()
    formData.append('name', projectName)
    formData.append('default_title_voice_id', audioVoiceId)
    formData.append('default_paragraph_voice_id', audioVoiceId)
    formData.append('default_model_id', modelId)

    // Create a new project in Elevenlabs Studio
    const response = await axios.post(
      getApiEndpoint('projects'),
      formData, 
      {
        headers: {
          'xi-api-key': apiKey.value,
          'Content-Type': 'multipart/form-data',
        },
      },
    )

    return response.data.project
  } 
  catch (error) {
    setError(`Error creating project: ${error}`)
    return null
  }
}

/**
 * Converts Elevenlabs Project
 * (Creates snapshots and readies the project for export)
 */
const convertProject = async (projectId: string) => {
  try {
    await axios.post(
      getApiEndpoint('convertProject', projectId),
      {},
      {
        headers: {
          'xi-api-key': apiKey.value,
          'Content-Type': 'application/json',
        },
      },
    )
  }
  catch (error) {
    setError(`Error converting project: ${error}`)
  }
}

/**
 * Returns the first chapter for the given ElevenLabs project
 */
const getProjectChapter = async (projectId: string): Promise<{ chapter_id: string} | null> => {
  try {
    // Load all chapters associated to the project ID
    const response = await axios.get(
      getApiEndpoint('chapters', projectId), 
      {
        headers: {
          'xi-api-key': apiKey.value,
        },
      })

    const chapters = response.data.chapters || []

    // Return the first chapter or null if no chapters were loaded
    return chapters.length > 0 ? chapters[0] : null
  } catch (error) {
    setError(`Error fetching project chapters: ${error}`)
    return null
  }
}

/**
 * Updates the chapter's content and converts it to audio
 */
const updateProjectChapter = async (
  projectId: string,
  chapterId: string,
  bodyContent: string,
  audioVoiceId: string,
  nodeType: string
) => {
  try {
    // Split content into multiple blocks (max 4,800 characters per block)
    const textBlocks = splitTextIntoChunks(bodyContent)

    // Format content to match ElevenLabs' expected API structure
    const chapterContent = {
      content: {
        blocks: textBlocks.map((text, index) => ({
          nodes: [
            {
              type: nodeType,
              text,
              voice_id: audioVoiceId,
            },
          ],
        })),
      },
    }

    // Update Chapter Content with multiple blocks
    await axios.post(
      getApiEndpoint('updateChapter', projectId, chapterId),
      chapterContent,
      {
        headers: {
          'xi-api-key': apiKey.value,
          'Content-Type': 'application/json',
        },
      },
    )

    // Convert chapter content to audio
    await axios.post(
      getApiEndpoint('convertChapter', projectId, chapterId),
      {},
      {
        headers: {
          'xi-api-key': apiKey.value,
          'Content-Type': 'application/json',
        },
      },
    )
  } catch (error) {
    setError(`Error updating or converting project chapter: ${error}`)
  }
}

/**
 * Returns a chapter's snapshot
 */
const getChapterSnapshots = async (projectId: string, chapterId: string, skipErrorCheck = false): Promise<{ chapter_snapshot_id: string } | null> => {
  try {
    // Load all snapshots for the associated chapter ID
    const response = await axios.get(
      getApiEndpoint('snapshots', projectId, chapterId),
      {
        headers: {
          'xi-api-key': apiKey.value,
        },
      },
    )
    
    const snapshot = response.data.snapshots || []

    // Return the latest snapshot or null if no snapshots were loaded
    return snapshot.length > 0 ? snapshot[0] : null 
  } catch (error) {
    if(!skipErrorCheck) {
      setError(`Error fetching chapter snapshots: ${error}`)
    }
    return null
  }
}
/**
 * Get the article's audio from the Elevenlabs project's snapshot
 */
const streamChapterAudio = async (projectId: string, chapterId: string, snapshotId: string): Promise<void> => {
  try {
    const response = await axios.post(
      getApiEndpoint('stream', projectId, chapterId, snapshotId),
      {}, // Empty object required for post
      {
        headers: {
          'xi-api-key': apiKey.value,
          'Content-Type': 'application/json',
        },
        responseType: 'blob',
      },
    )
    // Convert response to a blob URL
    audioUrl.value = URL.createObjectURL(response.data)
  } catch (error) {
    setError(`Error streaming chapter audio: ${error}`)
  }
}

/**
 * Checks if an Elevenlabs project was created within a specific time frame (measured in minutes)
 * Useful for knowing when to wait for snapshots to load
 */
const projectCreatedRecently = (timeFrameMinutes: number, projectCreatedAt: number | undefined) => {

  if (!projectCreatedAt) {
    console.error('projectCreatedAt is undefined')
    return false 
  }

  const now = Date.now()
  const timeFrameMs = timeFrameMinutes * 60_000 // Convert minutes to miliseconds
  const cutoffTime = now - timeFrameMs // Get the timestamp of `timeFrameMinutes` ago

  if (projectCreatedAt * 1000 >= cutoffTime) {
    return true
  } else {
    return false
  }
}

/**
 * Loads the article's pre-generated audio from ElevenLabs
 */
const loadAudio = async () => {
  // Set UI loading state
  loading.value = true
  hasError.value = false

  /**
   * LOAD PROJECT DATA
   */
  const projectData = await getProject(projectName.value)
  projectId.value = projectData?.project_id

  // If no project ID was found, generate the audio using Elevenlabs Studio
  if (!projectId.value) {
    generateAudio()
    return
  }

  /**
   * LOAD CHAPTER DATA
   */
  const projectChapter = await getProjectChapter(projectId.value)
  chapterId.value = projectChapter?.chapter_id

  // If no chapter ID was found, set an error and stop.
  if (!chapterId.value) {
    setError('Unable to load chapter data.')
    return
  }

  /**
   * LOAD SNAPSHOT AND STREAM AUDIO
   */
  let projectSnapshot

  // If the project was created recently, give the snapshot time to be generated
  if (projectCreatedRecently(10, projectData?.create_date_unix)) {
    let snapshotId
    let retries = 0
    const maxRetries = 120

    // Get the chapter's snapshot (This can take several minutes)
    while (!snapshotId && retries < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 5000))
      projectSnapshot = await getChapterSnapshots(projectId.value, chapterId.value, true)
      snapshotId = projectSnapshot?.chapter_snapshot_id
      retries++
    }

    if (!snapshotId) {
      setError('Snapshot did not load after chapter conversion.')
      return
    }
  } else {
    // Get the chapter's snapshot
    projectSnapshot = await getChapterSnapshots(projectId.value, chapterId.value)
    
    // If no snapshot loaded, stop.
    if (!projectSnapshot) {
      setError('Unable to load snapshot data.')
      return
    }
  }

  if (projectSnapshot) {
    // Stream the audio
    await streamChapterAudio(projectId.value, chapterId.value, projectSnapshot.chapter_snapshot_id)

    // Switch UI to show audio player
    showGenerateAudioBtn.value = false
    loading.value = false
    showAudioPlayer.value = true
  }
}

/**
 * Generates audio using Elevenlabs Studio API
 */
const generateAudio = async () => {
  // Make sure the article's character count is smaller than the allowed character limit
  if (articleCharCount.value > maxArticleCharAllowed.value) {
    setError(`The article's character count (${articleCharCount.value}) is greater than the max characters allowed to be converted (${maxArticleCharAllowed.value})`)
    return
  }

  loading.value = true

  try {
    /**
     * CREATE NEW ELEVENLABS PROJECT
     */
    const projectData = await createProject(
      projectName.value,
      audioVoiceId.value,
      modelId.value,
    )

    projectId.value = projectData?.project_id

    if (!projectId.value) {
      setError('Failed to create a new project.')
      return
    }
    
    /**
     * LOAD CHAPTER DATA
     */
    const projectChapter = await getProjectChapter(projectId.value)
    chapterId.value = projectChapter?.chapter_id

    // If no chapter ID was provided or found, stop.
    if (!chapterId.value) {
      setError('Unable to load chapter data.')
      return
    }

    /**
     * UPDATE AND CONVERT CHAPTER DATA
     */
    await updateProjectChapter(
      projectId.value,
      chapterId.value,
      bodyContent.value, 
      audioVoiceId.value,
      'tts_node',
    )
    
    /**
     * LOAD SNAPSHOT AND STREAM AUDIO
     */
    let snapshotId
    let projectSnapshot
    let retries = 0
    const maxRetries = 120

    // Get the chapter's snapshot (This can take several minutes)
    while (!snapshotId && retries < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 5000))
      projectSnapshot = await getChapterSnapshots(projectId.value, chapterId.value, true)
      snapshotId = projectSnapshot?.chapter_snapshot_id
      retries++
    }

    if (!snapshotId) {
      setError('Snapshot did not load after chapter conversion.')
      return
    }

    // Stream the audio
    await streamChapterAudio(projectId.value, chapterId.value, snapshotId)

    // Switch UI to show audio player
    showGenerateAudioBtn.value = false
    loading.value = false
    showAudioPlayer.value = true
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('Full API error response: ', JSON.stringify(error.response?.data ?? 'No response data', null, 2))
    } else if (error instanceof Error) {
      console.error('Error generating and saving audio: ', error.message)
    } else {
      console.error('An unknown error occurred: ', error)
    }
    loading.value = false
    hasError.value = true
  }
}

/**
 * Controller for managing Elevenlabs endpoint paths
 */
const getApiEndpoint = (
  type: 'projects' | 'convertProject' | 'chapters' | 'updateChapter' | 'convertChapter' | 'snapshots' | 'stream', 
  projectId?: string, 
  chapterId?: string, 
  snapshotId?: string,
) => {
  const baseUrl = 'https://api.elevenlabs.io/v1/studio'

  switch (type) {
    case 'projects':
      return `${baseUrl}/projects`
    case 'convertProject':
      if (!projectId) throw new Error('Project ID is required for project convert endpoint')
      return `${baseUrl}/projects/${projectId}/convert`
    case 'chapters':
      if (!projectId) throw new Error('Project ID is required to get chapters endpoint')
      return `${baseUrl}/projects/${projectId}/chapters`
    case 'updateChapter':
      if (!projectId || !chapterId) throw new Error('Project ID and Chapter ID are required for updating chapter endpoint')
      return `${baseUrl}/projects/${projectId}/chapters/${chapterId}`
    case 'convertChapter':
      if (!projectId || !chapterId) throw new Error('Project ID and Chapter ID are required for chapter convert endpoint')
      return `${baseUrl}/projects/${projectId}/chapters/${chapterId}/convert`
    case 'snapshots':
      if (!projectId || !chapterId) throw new Error('Project ID and Chapter ID are required for snapshots endpoint')
      return `${baseUrl}/projects/${projectId}/chapters/${chapterId}/snapshots`
    case 'stream':
      if (!projectId || !chapterId || !snapshotId) throw new Error('Project ID, Chapter ID, and Snapshot ID are required for streaming endpoint')
      return `${baseUrl}/projects/${projectId}/chapters/${chapterId}/snapshots/${snapshotId}/stream`
    default:
      throw new Error('Invalid API type')
  }
}

/**
 * Splits content into specifically sized chunks
 */
const splitTextIntoChunks = (text: string, maxLength = 4800): string[] => {
  const chunks: string[] = []
  let currentChunk = ''
  const words = text.split(' ')

  for (const word of words) {
    // If adding the word exceeds max length, push current chunk and start a new one
    if ((currentChunk + ' ' + word).length > maxLength) {
      chunks.push(currentChunk.trim()) // Remove extra spaces before saving
      currentChunk = word
    } else {
      currentChunk += ' ' + word
    }
  }

  // Push the final chunk if there's remaining text
  if (currentChunk.trim()) {
    chunks.push(currentChunk.trim())
  }

  return chunks
}

/**
 * Handles error UI and console logging
 */
const setError = (errorMessage: string) => {
  console.error(errorMessage)
  hasError.value = true
  loading.value = false
}

onMounted(() => {
  // Add the article title to the body content so it is included in the audio
  bodyContent.value = articleTitle.value + ' ' + bodyContent.value
})

return (_ctx: any,_cache: any) => {
  return (loading.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, _hoisted_3))
    : (hasError.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(errorMessage.value), 1))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (showGenerateAudioBtn.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                id: "elevenlabs-listen-here",
                class: "btn btn--filled btn--md btn--primary",
                onClick: loadAudio
              }, _toDisplayString(BUTTON_TEXT)))
            : _createCommentVNode("", true),
          (showAudioPlayer.value)
            ? (_openBlock(), _createElementBlock("audio", {
                key: 1,
                src: audioUrl.value,
                controls: ""
              }, null, 8, _hoisted_6))
            : _createCommentVNode("", true)
        ]))
}
}

})